import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-summary-panel>` says about itself.
 *
 * Summarize is the one capability that answers in prose, and prose is the one output the platform's
 * contracts cannot check — the `text` contract treats the string as the instance and never parses
 * it, so citations are not enforced anywhere underneath. The architecture is explicit that a surface
 * needing cited summaries establishes the citations itself. This is that surface.
 *
 * Three decisions worth knowing before placing it.
 *
 * Citations arrive as MARKERS in the prose, not as character offsets. An offset is correct only
 * against the exact string the model emitted, so anything that trims or re-wraps the text slides
 * every citation silently onto the wrong words.
 *
 * “Uncited” is one mechanical rule, deliberately narrower than the intent: a sentence with a digit
 * in it carrying no marker that RESOLVES to a supplied citation. Resolution rather than bracket
 * shape, or a marker-shaped aside would pass a number off as traceable. It will not catch every
 * uncited assertion, and it will not cry wolf — and a check that cries wolf gets switched off.
 *
 * An empty summary draws a REFUSAL, not an empty box. The `text` contract already reports an empty
 * body as `empty_output` on the grounds that a blank answer is worse than a refusal; the panel
 * honours the same judgement on screen.
 */
const docs: ComponentDocs = {
  aliases: ['summary', 'summarize', 'ai summary', 'cited summary', 'record summary', 'citations'],

  answers: [
    'read a generated summary of a record and its related lists',
    'check which field a number in a summary came from',
    'see which claims in a summary are not backed by a field',
    'refuse to publish a summary that cites nothing',
  ],

  placements: ['recordPage', 'appPage', 'homePage'],

  whenToUse:
    'Wherever a generated summary is read for a decision, and somebody will be asked where a number ' +
    'in it came from.',

  insteadUse: [
    {
      name: 'MessageBubble',
      when:
        'the prose is one turn of a conversation. A bubble is a message with an author and a time; ' +
        'this is an answer with provenance.',
    },
    {
      name: 'NoticeBanner',
      when: 'the text is the platform speaking — a warning, a state, an instruction — rather than a generated answer.',
    },
    {
      name: 'DetailSection',
      when: 'the facts can be shown as fields. A summary is for when the shape of the answer is prose.',
    },
  ],

  inputs: [
    {
      name: 'prose',
      attribute: 'prose',
      type: 'text',
      description:
        'The summary body, with citation markers written in square brackets — `[1]`, `[rev]`. Empty or ' +
        'whitespace-only draws the refusal line rather than an empty panel.',
    },
    {
      name: 'citations',
      type: 'json',
      description:
        'The `fieldRef` citations: `{ marker, object, field, recordId?, value? }[]`. `marker` is the ' +
        'token as it appears in the prose WITHOUT its brackets. A property, because it is structured ' +
        'data. A citation whose marker is nowhere in the prose is reported through `orphaned` rather ' +
        'than dropped.',
      default: [],
    },
    {
      name: 'refusalText',
      attribute: 'refusal-text',
      type: 'text',
      description: 'What to say when no summary was produced.',
      default: 'No summary was produced. An empty answer is reported rather than shown.',
    },
    {
      name: 'uncitedLabel',
      attribute: 'uncited-label',
      type: 'text',
      description: 'How an uncited claim is announced to a screen reader, before the sentence itself.',
      default: 'Uncited claim',
    },
  ],

  events: [
    {
      name: 'caos-summary-cite',
      when: 'a citation marker is pressed',
      carries: '`{ object, field, recordId, marker }` — the field to open; `recordId` is null when the surface’s own record is meant',
    },
  ],

  state: [
    {
      name: 'uncited',
      type: 'string[]',
      description:
        'The sentences carrying a number and no citation. Read it to GATE publishing a summary rather ' +
        'than only to colour it — which is the difference between a warning and a control.',
    },
    {
      name: 'orphaned',
      type: 'CaosSummaryCitation[]',
      description:
        'Citations whose marker appears nowhere in the prose, so a reader can never reach them. This is ' +
        'how a summary ends up looking fully cited while half its citations point at nothing.',
    },
    {
      name: 'fullyCited',
      type: 'boolean',
      description: 'Every quantitative claim carries a citation, and every citation is reachable.',
    },
    {
      name: 'empty',
      type: 'boolean',
      description: 'There is no summary — the contract’s `empty_output`, readable without parsing the panel.',
    },
  ],

  parts: [
    { part: 'summary', is: 'The outer column.' },
    { part: 'prose', is: 'The summary body.', when: 'a summary was produced' },
    { part: 'sentence', is: 'One sentence of the body.' },
    { part: 'uncited', is: 'A sentence carrying a number and no citation.', when: 'at least one claim is uncited' },
    { part: 'citation', is: 'One citation marker, as a control that opens the field.' },
    { part: 'verdict', is: 'The line under the prose saying what is and is not traceable.' },
    { part: 'refusal', is: 'The “no summary was produced” panel.', when: '`prose` is empty' },
  ],

  accessibility:
    'Each citation is a real button whose accessible name reads the field it opens and, where one was ' +
    'supplied, the value it was read from — so a reader can check a claim without leaving the panel. ' +
    'An uncited sentence carries an `aria-label` naming it as an uncited claim and then repeating the ' +
    'sentence, because the tint behind it is not a word and a monochrome display would show nothing ' +
    'at all. The verdict line is a `role="status"` live region, so a summary that arrives short of ' +
    'citations is announced rather than only redrawn.',

  examples: [
    {
      key: 'fully-cited',
      label: 'Every number traced to a field',
      summary:
        'The case the capability is for. Each figure carries a marker that opens the field it came from, ' +
        'and the verdict line says so — so a reader who is going to repeat one of these numbers can ' +
        'check it first.',
      placement: {
        component: 'caos-summary-panel',
        inputs: {
          prose:
            'The account has been open since 2019 [1] and carries 4 active contracts [2]. ' +
            'Its last payment arrived on 12 October [3].',
          citations: [
            { marker: '1', object: 'account', field: 'created_at', value: '2019-03-04' },
            { marker: '2', object: 'account', field: 'active_contract_count', value: '4' },
            { marker: '3', object: 'payment', field: 'received_at', recordId: 'pay_9912', value: '2026-10-12' },
          ],
        },
      },
      stage: { needsWidth: 560 },
    },
    {
      key: 'uncited-claim',
      label: 'A number nothing backs',
      summary:
        'The middle sentence states a figure and cites nothing, so it is marked and counted. This is the ' +
        'failure the panel exists to make visible: the prose reads exactly as authoritatively as the ' +
        'cited sentences around it.',
      placement: {
        component: 'caos-summary-panel',
        inputs: {
          prose:
            'The account has been open since 2019 [1]. It is responsible for roughly 18% of regional ' +
            'revenue. Its last payment arrived on 12 October [3].',
          citations: [
            { marker: '1', object: 'account', field: 'created_at', value: '2019-03-04' },
            { marker: '3', object: 'payment', field: 'received_at', recordId: 'pay_9912' },
          ],
        },
      },
      stage: { needsWidth: 560 },
    },
    {
      key: 'orphaned-citation',
      label: 'A citation pointing at nothing',
      summary:
        'Four citations were supplied and one names a marker the prose never uses. It is reported rather ' +
        'than dropped, because a long citation list beside clean prose is exactly how nobody notices.',
      placement: {
        component: 'caos-summary-panel',
        inputs: {
          prose: 'The account has been open since 2019 [1] and carries 4 active contracts [2].',
          citations: [
            { marker: '1', object: 'account', field: 'created_at' },
            { marker: '2', object: 'account', field: 'active_contract_count' },
            { marker: '7', object: 'account', field: 'industry' },
          ],
        },
      },
      stage: { needsWidth: 560 },
    },
    {
      key: 'empty-output',
      label: 'No summary at all',
      summary:
        'The contract reports an empty body as `empty_output` rather than letting it pass as an answer, ' +
        'and the panel says the same thing on screen instead of drawing an empty box that reads as ' +
        'still loading.',
      placement: {
        component: 'caos-summary-panel',
        inputs: { prose: '   ' },
      },
      stage: { needsWidth: 480 },
    },
  ],
};

export default docs;
